import type { GlobalAssistantPageCatalogEntry } from './types';

export const DATA_DICTIONARY_CATALOG_OPTION_ID = '__std__:app:data-dictionary';

export const GLOBAL_ASSISTANT_PAGE_CATALOG = [
  // ---------- COMPANY ----------
  {
    optionId: '__std__:company:compositions',
    revelioPageIds: ['company-composition-v2'],
    entity: 'company',
    pageName: 'Company Overview',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Workforce breakdowns for one company by standard dimensions',
        'Distribution of employees across supported dimensions such as function/role, seniority, and geography',
        'Headline workforce metrics for one company — headcount, growth rate, hiring rate and attrition rate',
        'A high-level snapshot of an employer before drilling into a specific area',
      ],
      bestForExamples: [
        'what is the role composition at Meta',
        'what roles make up Tesla’s manufacturing workforce',
        'what functions make up Microsoft’s AI org',
        'break down Apple’s workforce by seniority',
        'how is IBM split across geographies',
        'give me an overview of Google',
        'what does Amazon look like at a high level',
      ],
      notFor: [
        'Hiring/attrition flows over time (use Transitions)',
        'Comparing the composition of two companies side by side',
        'A specific custom dimension the standard breakdowns do not cover',
        'Role result-set questions with no strong company-anchored composition framing (use Role Overview and apply the company as a filter)',
        'Gender or ethnicity breakdown charts — this page has none; gender and ethnicity remain available as filters',
      ],
    },
  },
  {
    optionId: '__std__:company:diversity',
    revelioPageIds: ['company-diversity'],
    entity: 'company',
    pageName: 'Company Diversity',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Gender and ethnicity composition for one company',
        'Gender and ethnicity headcount share trends over time',
        'Gender and ethnicity representation by role, with company or industry comparisons',
      ],
      bestForExamples: [
        'show the gender breakdown at Google',
        'how has female representation changed at Microsoft',
        'compare ethnic representation by role at Amazon',
        'how diverse is Meta’s workforce',
      ],
      notFor: [
        'Employee perceptions of diversity and inclusion (use Sentiment)',
        'Gender pay equity or a narrative assessment with recommendations (use the Gender Diversity report)',
        'General workforce breakdowns unrelated to gender or ethnicity (use Company Overview)',
      ],
    },
  },
  {
    optionId: '__std__:company:transitions',
    revelioPageIds: ['company-transitions-v2'],
    entity: 'company',
    pageName: 'Company Transitions',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Hiring and attrition trends for one company',
        'Inflow/outflow talent destinations and sources for the selected employer',
      ],
      bestForExamples: [
        'what is the attrition trend at Google',
        'where do people go after leaving Microsoft',
        'show hiring and attrition flows for Amazon',
      ],
      notFor: [
        'A single attrition-rate number for a specific role (often a chart)',
        'Comparing flows across two companies side by side',
        'Why attrition is changing (causal — custom page)',
      ],
    },
  },
  {
    optionId: '__std__:company:postings',
    revelioPageIds: ['company-postings'],
    entity: 'company',
    pageName: 'Company Demand',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Active posting volume and trend for one company',
        'What roles or areas an employer is hiring for via postings',
      ],
      bestForExamples: [
        'what is Google hiring for right now',
        'show me Microsoft’s job postings trend',
        'how many open roles does Amazon have',
      ],
      notFor: [
        'Actual hires/headcount (postings ≠ hires; use Transitions)',
        'Browsing or listing individual postings (use Postings Discovery)',
        'Comparing posting volume across two companies side by side',
      ],
    },
  },
  {
    optionId: '__std__:company:pay',
    revelioPageIds: ['company-pay'],
    entity: 'company',
    pageName: 'Company Compensation',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Pay levels and benchmarks for one company',
        'How that company’s own pay varies by role and by geography, and the spread of its pay distribution',
      ],
      bestForExamples: [
        'what does Google pay',
        'show me compensation benchmarks for Microsoft',
        'which roles are paid the most at Amazon',
      ],
      notFor: [
        'Side-by-side pay of two named companies',
        'Pay for one specific role at one company (often a chart, or the Compensation report)',
        'Sales-specific comp (use the Sales Compensation report)',
      ],
    },
  },
  {
    optionId: '__std__:company:sentiment',
    revelioPageIds: ['company-sentiment'],
    entity: 'company',
    pageName: 'Company Sentiment',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Employee sentiment signals, review themes, and employee-experience drivers for one company',
        'That company’s own sentiment trend over time (the page’s Over Time toggle)',
        'Useful context for why employees may be leaving or retention pressure may be rising',
      ],
      bestForExamples: [
        'how do employees feel about Google',
        'show me sentiment for Microsoft',
        'what are the review themes at Amazon',
        'show employee sentiment across the retail sector',
      ],
      notFor: [
        'Employer prestige / reputation ranking (Prestige report)',
        'Side-by-side sentiment of two named companies',
      ],
    },
  },
  {
    optionId: '__std__:company:screener',
    revelioPageIds: ['company-screener-v2'],
    entity: 'company',
    pageName: 'Company Discovery',
    requiresResolvedEntity: false,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Rank and filter companies by supported workforce metrics',
        'Find companies matching size/growth/sector/geo criteria',
      ],
      bestForExamples: [
        'which companies have the highest attrition in tech',
        'find the largest employers in healthcare',
        'top companies hiring data scientists',
        'which companies hire the most AI engineers',
      ],
      notFor: [
        'Metrics for one specific named company',
        'An industry or sector as the analytical subject rather than a filter on company results',
        'Searching for individual people (use Individual Discovery)',
        'Ranking roles, skills, or geographies where companies are not the answer set',
      ],
    },
  },
  // ---------- INDUSTRY ----------
  {
    optionId: '__std__:industry:compositions',
    revelioPageIds: ['industry-composition'],
    entity: 'industry',
    pageName: 'Industry Overview',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Headline workforce metrics for one industry or employer market',
        'Workforce breakdowns by role, geography, skill, seniority, and other supported dimensions',
        'A high-level industry workforce overview before drilling into a topic',
      ],
      bestForExamples: [
        'give me an overview of the fintech industry',
        'show an overview of healthcare workforce composition',
        'how is the energy sector workforce structured',
      ],
      notFor: [
        'Ranking or screening companies within the industry (use Company Discovery)',
        'Returning ranked roles or geographies (use the corresponding grounded breakdown flow)',
        'Hiring/attrition flows over time (use Industry Transitions)',
      ],
    },
  },
  {
    optionId: '__std__:industry:transitions',
    revelioPageIds: ['industry-transitions'],
    entity: 'industry',
    pageName: 'Industry Transitions',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Hiring and attrition trends for one industry or employer market',
        'Previous and next industries for the selected industry workforce; population filters do not change the destination dimension',
      ],
      bestForExamples: [
        'show hiring and attrition trends for software',
        'which industries do people leaving retail move to',
        'how are healthcare hiring flows changing',
      ],
      notFor: [
        'A single hiring-rate or attrition-rate value',
        'A company ranking within the industry',
        'Why attrition is changing when several diagnostic views are required',
      ],
    },
  },
  {
    optionId: '__std__:industry:postings',
    revelioPageIds: ['industry-postings'],
    entity: 'industry',
    pageName: 'Industry Demand',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Posting volume and demand trends for one industry or employer market',
        'Which roles are driving industry job-posting demand',
      ],
      bestForExamples: [
        'show job-posting demand in healthcare',
        'how are software job postings changing',
        'what roles is the energy sector advertising',
      ],
      notFor: [
        'Actual hiring and attrition flows (use Industry Transitions)',
        'Browsing individual postings (use Postings Discovery)',
        'Ranking companies by postings (use Company Discovery)',
        'Which geographies are driving posting demand — the page breaks postings down by role only (use Geography Demand)',
      ],
    },
  },
  {
    optionId: '__std__:industry:pay',
    revelioPageIds: ['industry-pay'],
    entity: 'industry',
    pageName: 'Industry Compensation',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Pay levels and compensation benchmarks for one industry or employer market',
        'Pay variation across supported roles and geographies within an industry',
      ],
      bestForExamples: [
        'show compensation benchmarks for healthcare',
        'what does the fintech industry pay',
        'salary benchmarks across the energy sector',
      ],
      notFor: [
        'Ranking roles or companies by pay',
        'Side-by-side comparison of several industries',
        'Labor/payroll cost analysis requiring custom diagnostics',
      ],
    },
  },
  {
    optionId: '__std__:industry:market-conditions',
    revelioPageIds: ['industry-market-conditions'],
    entity: 'industry',
    pageName: 'Industry Market Conditions',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Labor-market tightness for an industry or employer market',
        'Demand/supply ratio, wage pressure, and time-to-fill context',
      ],
      bestForExamples: [
        'how tight is the fintech labor market',
        'is healthcare difficult to hire in',
        'show market conditions in the software sector',
      ],
      notFor: [
        'Actual hiring/attrition flows (use Industry Transitions)',
        'A company ranking within the industry',
        'Several unrelated workforce questions in one request',
      ],
    },
  },
  {
    optionId: '__std__:industry:sentiment',
    revelioPageIds: ['industry-sentiment'],
    entity: 'industry',
    pageName: 'Industry Sentiment',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Employee sentiment and review signals for one industry or employer market',
        'Sentiment trend, themes, and retention-pressure context',
      ],
      bestForExamples: [
        'how is sentiment across the energy sector',
        'what do employees think about working in retail',
        'show sentiment in the healthcare industry',
      ],
      notFor: [
        'Ranking companies by sentiment (use Company Discovery)',
        'A single sentiment-aspect value',
        'A broad causal diagnosis requiring multiple lenses',
      ],
    },
  },
  // ---------- GEOGRAPHY ----------
  {
    optionId: '__std__:geography:compositions',
    revelioPageIds: ['geography-composition-v2'],
    entity: 'geography',
    pageName: 'Geography Overview',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Workforce breakdowns for one geography by standard dimensions',
        'Industry/function/seniority mix of a regional market',
        'Headline workforce metrics for one geography — headcount, growth rate, hiring rate and attrition rate',
        'A high-level snapshot of a regional labor market before drilling into a specific area',
      ],
      bestForExamples: [
        'break down the London workforce by industry',
        'what roles dominate the Austin labor market',
        'seniority mix in the New York tech scene',
        'give me an overview of the London labor market',
        'what does the Bay Area workforce look like',
      ],
      notFor: [
        'Hiring/attrition flows over time (Transitions)',
        'Comparing two locations side by side',
        'Gender or ethnicity breakdown charts — this page has none; gender and ethnicity remain available as filters',
      ],
    },
  },
  {
    optionId: '__std__:geography:transitions',
    revelioPageIds: ['geography-transitions-v2'],
    entity: 'geography',
    pageName: 'Geography Transitions',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Talent migration into and out of one geography, ranked by previous or next geography',
        'Hiring and attrition dynamics for a regional market',
      ],
      bestForExamples: [
        'which locations do people move to when they leave San Francisco',
        'talent inflow to Miami',
        'hiring vs attrition in London',
      ],
      notFor: [
        'Comparing flows across two locations side by side',
        'Ranking destination employers or roles for a location',
        'Why migration is changing (causal — custom page)',
      ],
    },
  },
  {
    optionId: '__std__:geography:postings',
    revelioPageIds: ['geography-postings'],
    entity: 'geography',
    pageName: 'Geography Demand',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Active posting volume and trend for one geography',
        'What a regional market is hiring for',
      ],
      bestForExamples: [
        'what is being hired for in Seattle',
        'job posting trends in London',
        'demand for engineers in Austin',
      ],
      notFor: [
        'Actual hires/headcount (postings ≠ hires)',
        'Browsing or listing individual postings (use Postings Discovery)',
        'Comparing posting volume across two locations',
      ],
    },
  },
  {
    optionId: '__std__:geography:pay',
    revelioPageIds: ['geography-pay'],
    entity: 'geography',
    pageName: 'Geography Compensation',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Pay levels and benchmarks for one geography',
        'Cost-of-talent for a regional market',
      ],
      bestForExamples: [
        'what is the average pay in London',
        'compensation levels in the Bay Area',
        'how expensive is talent in New York',
      ],
      notFor: [
        'Side-by-side pay of two named locations',
        'Pay at a specific company (use Company Compensation)',
      ],
    },
  },
  {
    optionId: '__std__:geography:market-conditions',
    revelioPageIds: ['geography-market-conditions'],
    entity: 'geography',
    pageName: 'Geography Market Conditions',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'How tight the labor market is for one geography — market tightness and demand/supply ratio',
        'The same tightness metrics for up to 6 geographies side by side',
        'Wage pressure and time to fill for a regional market',
        'Which companies are competing for talent there via postings',
      ],
      bestForExamples: [
        'how hard is it to hire in Austin',
        'how tight is the London labor market',
        'how tight are the Austin and Denver labor markets',
        'what is the demand/supply balance for talent in Seattle',
      ],
      notFor: [
        'Comparing more than 6 locations at once (the primary filter caps at 6)',
        'Posting volume, trends, or lists of open roles (use Geography Demand)',
        'Pay levels or compensation benchmarks — wage pressure here is hiring-market pressure, not pay (use Geography Compensation)',
      ],
    },
  },
  {
    optionId: '__std__:geography:sentiment',
    revelioPageIds: ['geography-sentiment'],
    entity: 'geography',
    pageName: 'Geography Sentiment',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Employee sentiment signals and experience themes for one geography',
        'Regional sentiment trend',
        'Useful context for why workers in a location or defined employee group may be leaving',
      ],
      bestForExamples: [
        'how do workers feel in London',
        'sentiment in the Austin tech market',
      ],
      notFor: [
        'Company-specific sentiment (use Company Sentiment)',
        'Comparing two locations side by side',
      ],
    },
  },
  {
    optionId: '__std__:geography:screener',
    revelioPageIds: ['geography-screener-v2'],
    entity: 'geography',
    pageName: 'Geography Discovery',
    requiresResolvedEntity: false,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Rank and filter geographies by supported workforce metrics',
        'Find markets matching size/growth/cost criteria, optionally within a parent geography',
      ],
      bestForExamples: [
        'which cities have the most software engineers',
        'cheapest markets for hiring designers',
        'fastest-growing tech hubs',
        'fastest-growing cities within a country',
      ],
      notFor: [
        'Metrics for one specific named location',
        'Searching for individual people (Individual Discovery)',
      ],
    },
  },
  // ---------- ROLE ----------
  {
    optionId: '__std__:role:compositions',
    revelioPageIds: ['role-composition-v3'],
    entity: 'role',
    pageName: 'Role Overview',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Headline workforce metrics for one role — headcount, growth rate, hiring rate and attrition rate',
        'How a role breaks down into the narrower roles beneath it in the role taxonomy, up to two levels down, sized by headcount',
        'Which skills the people in a role hold, and which work activities they perform',
        'Most common role mix within a company, industry, geography, or other held-constant filter',
        'A high-level snapshot of an occupation before drilling into a specific area',
      ],
      bestForExamples: [
        'which specialisms make up software engineering',
        'what roles exist in the taxonomy',
        'show me the job architecture',
        'skill mix of product managers',
        'what work do nurses spend their time on',
        'which roles are most common at OpenAI',
        'give me an overview of data scientists',
        'what does the nursing occupation look like',
      ],
      notFor: [
        'Hiring/attrition flows over time (Transitions)',
        'Comparing two roles side by side',
        'Composition questions with a stronger company/geography anchor when that entity’s Overview page better answers the requested breakdown',
        'Industry, geography, seniority, gender or ethnicity breakdowns of a role — this page has none; all remain available as filters',
        'Headcount or rate trends over time — the page shows one snapshot and has no Over Time view',
      ],
    },
  },
  {
    optionId: '__std__:role:transitions',
    revelioPageIds: ['role-transitions-v2'],
    entity: 'role',
    pageName: 'Role Transitions',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Career paths into and out of one role — the previous and next roles people move from and to',
        'Hiring and attrition dynamics for an occupation',
      ],
      bestForExamples: [
        'which roles do data scientists move to',
        'which roles do software engineers come from',
        'what roles do consultants move into next',
      ],
      notFor: [
        'Comparing flows across two roles side by side',
        'Why mobility is changing (causal — custom page)',
        'Ranking destination employers, industries or geographies for this role — flow rows rank roles; population filters do not change that dimension',
      ],
    },
  },
  {
    optionId: '__std__:role:postings',
    revelioPageIds: ['role-postings'],
    entity: 'role',
    pageName: 'Role Demand',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Demand (posting volume) and trend for one role',
        'Where and by whom a role is being hired',
      ],
      bestForExamples: [
        'demand for data scientists',
        'job postings trend for software engineers',
        'how many nursing jobs are being posted',
      ],
      notFor: [
        'Actual hires/headcount (postings ≠ hires)',
        'Browsing or listing individual postings (use Postings Discovery)',
        'Comparing posting volume across two roles',
        'Ranking companies by how much they hire/post for a role (use Company Discovery)',
      ],
    },
  },
  {
    optionId: '__std__:role:pay',
    revelioPageIds: ['role-pay'],
    entity: 'role',
    pageName: 'Role Compensation',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Pay levels and benchmarks for one role',
        'How a role is paid across the market',
      ],
      bestForExamples: [
        'what do data scientists earn',
        'pay for software engineers',
        'salary benchmarks for product managers',
      ],
      notFor: [
        'Side-by-side pay of two named roles',
        'Pay for a role at one company / one country (Compensation Benchmarking report)',
      ],
    },
  },
  {
    optionId: '__std__:role:market-conditions',
    revelioPageIds: ['role-market-conditions'],
    entity: 'role',
    pageName: 'Role Market Conditions',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'How tight the labor market is for one role — market tightness and demand/supply ratio',
        'The same tightness metrics for up to 6 roles side by side',
        'Wage pressure and time to fill for an occupation',
        'Which companies are competing for that talent via postings',
      ],
      bestForExamples: [
        'how hard is it to hire nurses right now',
        'how tight is the market for data scientists',
        'compare hiring difficulty for nurses and physical therapists',
        'is it getting harder to hire software engineers',
      ],
      notFor: [
        'Comparing more than 6 roles at once (the primary filter caps at 6)',
        'Posting volume, trends, or lists of open roles (use Role Demand)',
        'Pay levels or compensation benchmarks — wage pressure here is hiring-market pressure, not pay (use Role Compensation)',
      ],
    },
  },
  {
    optionId: '__std__:role:sentiment',
    revelioPageIds: ['role-sentiment'],
    entity: 'role',
    pageName: 'Role Sentiment',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Sentiment signals and employee-experience drivers for one role',
        'How satisfied an occupation is, with trend',
        'Useful context for why people in a role may be leaving or under retention pressure',
      ],
      bestForExamples: [
        'how do software engineers feel about their jobs',
        'sentiment among nurses',
      ],
      notFor: [
        'Company-specific sentiment (use Company Sentiment)',
        'Comparing two roles side by side',
      ],
    },
  },
  {
    optionId: '__std__:role:screener',
    revelioPageIds: ['role-screener-v2'],
    entity: 'role',
    pageName: 'Role Discovery',
    requiresResolvedEntity: false,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Rank and filter roles by supported workforce metrics',
        'Find occupations matching demand/pay/growth criteria',
      ],
      bestForExamples: [
        'which roles are growing fastest',
        'which roles have the highest attrition',
        'highest-paid occupations in tech',
      ],
      notFor: [
        'Metrics for one specific named role',
        'Searching for individual people (Individual Discovery)',
        'Ranking companies that hire a role (use Company Discovery)',
        'Role composition/mix questions within a held-constant company (use Role Overview)',
      ],
    },
  },
  // ---------- SKILL ----------
  // Skill is a unit of analysis in its own right: the primary filter is the
  // taxonomy-aware skill picker, so these pages answer "what does this skill
  // look like" rather than "which skills does X have" — that breakdown is a
  // dimension on the other entities' Overview pages.
  {
    optionId: '__std__:skill:compositions',
    revelioPageIds: ['skill-composition'],
    entity: 'skill',
    pageName: 'Skill Overview',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Workforce breakdowns for one skill by role and by work activity',
        'Which roles hold a skill, and which work activities the people holding it perform',
        'How a skill breaks down into the narrower skills beneath it in the skill taxonomy, sized by headcount',
        'Headline workforce metrics for a skill — headcount, growth rate, hiring rate and attrition rate',
        'A high-level snapshot of a skill before drilling into a specific area',
      ],
      bestForExamples: [
        'which roles have python skills',
        'what work do people with machine learning skills do',
        'how many people hold each skill within data analytics',
        'give me an overview of project management as a skill',
        'how fast is the number of people with cybersecurity skills growing',
      ],
      notFor: [
        'The skill mix OF a company, industry, role or geography — that is a breakdown on that entity’s Overview page',
        'Comparing two skills side by side',
        'Employee sentiment — skill has no Sentiment page',
        'Hiring/attrition flows between employers — skill has no Transitions page',
        'Industry, geography, seniority, gender or ethnicity breakdowns of a skill — this page has none; all remain available as filters',
      ],
    },
  },
  {
    optionId: '__std__:skill:postings',
    revelioPageIds: ['skill-postings'],
    entity: 'skill',
    pageName: 'Skill Demand',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Demand for a skill in the job market — posting volume and trend',
        'Advertised salary and time to fill for postings asking for a skill',
        'Which companies are hiring for a skill',
      ],
      bestForExamples: [
        'how many jobs ask for kubernetes',
        'is demand for prompt engineering growing',
        'who is hiring for data analytics skills',
        'what do postings pay for machine learning',
      ],
      notFor: [
        'The existing workforce holding a skill (Skill Overview)',
        'Browsing or searching individual postings (Postings Discovery)',
        'Comparing posting volume across two skills',
      ],
    },
  },
  {
    optionId: '__std__:skill:pay',
    revelioPageIds: ['skill-pay'],
    entity: 'skill',
    pageName: 'Skill Compensation',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'What a skill pays — base salary, total compensation and hourly rates',
        'Pay for a skill by company and by geography',
        'The pay distribution across people holding a skill',
      ],
      bestForExamples: [
        'what does a machine learning skill pay',
        'how much do people with kubernetes skills earn',
        'pay for data analytics by geography',
        'hourly rates for kubernetes skills',
      ],
      notFor: [
        'Side-by-side pay of two named skills',
        'Advertised pay on open postings (Skill Demand)',
        'Hiring-market pressure — wage pressure lives on Skill Market Conditions',
      ],
    },
  },
  {
    optionId: '__std__:skill:market-conditions',
    revelioPageIds: ['skill-market-conditions'],
    entity: 'skill',
    pageName: 'Skill Market Conditions',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'How tight the labor market is for a skill — market tightness and demand/supply ratio',
        'The same tightness metrics for up to 6 skills side by side',
        'Wage pressure and time to fill for a skill',
        'Which companies are competing for that skill via postings',
      ],
      bestForExamples: [
        'how hard is it to hire people with machine learning skills',
        'how tight is the market for cybersecurity skills',
        'compare hiring difficulty for python and rust',
        'is it getting harder to hire for data engineering skills',
      ],
      notFor: [
        'Comparing more than 6 skills at once (the primary filter caps at 6)',
        'Posting volume or posting trends (use Skill Demand); individual open postings (use Postings Discovery)',
        'Pay levels — wage pressure here is hiring-market pressure, not pay (use Skill Compensation)',
      ],
    },
  },
  // ---------- ACTIVITY ----------
  // Activity is a unit of analysis in its own right: the primary filter is the
  // activity picker — work tasks people perform, such as processing payroll —
  // so these pages answer "what does the workforce doing this work look like",
  // not "which activities does X do".
  {
    optionId: '__std__:activity:compositions',
    revelioPageIds: ['activity-composition'],
    entity: 'activity',
    pageName: 'Activity Overview',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Headline workforce metrics for an activity — headcount, growth rate, hiring rate and attrition rate',
        'Which roles and skills make up the workforce performing an activity',
        'How an activity breaks down into the narrower activities beneath it in the activity taxonomy, sized by headcount',
        'A high-level snapshot of an activity before drilling into a specific area',
      ],
      bestForExamples: [
        'how many people process payroll',
        'which roles review contracts',
        'what skills do people who negotiate supplier contracts have',
        'how fast is the number of people reviewing code growing',
        'give me an overview of patient intake work',
      ],
      notFor: [
        'The activity mix OF a company, role, skill, industry or geography — this page profiles one activity’s own workforce; a skill’s or a role’s activity mix is the Activities chart on its Skill Overview or Role Overview page, and no standard page breaks a company, industry or geography down by activity',
        'Comparing two activities side by side',
        'One company’s workforce for an activity (“payroll processing at Google”) — activity pages have no Company filter, so a named company cannot be applied here',
        'Industry, geography, seniority, gender or ethnicity breakdowns of an activity — this page has none; all remain available as filters',
        'Employee sentiment — activity has no Sentiment page',
        'Hiring/attrition flows between employers — activity has no Transitions page',
      ],
    },
  },
  {
    optionId: '__std__:activity:postings',
    revelioPageIds: ['activity-postings'],
    entity: 'activity',
    pageName: 'Activity Demand',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Demand for an activity in the job market — posting volume and trend',
        'Advertised salary and time to fill for postings involving an activity',
        'Which companies are hiring for an activity',
      ],
      bestForExamples: [
        'how many jobs involve processing payroll',
        'is demand for incident response work growing',
        'who is hiring for contract negotiation',
        'what do postings pay for preparing financial statements',
      ],
      notFor: [
        'The existing workforce performing an activity (Activity Overview)',
        'Browsing or searching individual postings (Postings Discovery)',
        'Comparing posting volume across two activities',
        'Postings for an activity at one company — activity pages have no Company filter, so a named company cannot be applied here',
      ],
    },
  },
  {
    optionId: '__std__:activity:pay',
    revelioPageIds: ['activity-pay'],
    entity: 'activity',
    pageName: 'Activity Compensation',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'What an activity pays — hourly, base and total compensation',
        'Pay for an activity by company and by geography',
        'The pay distribution across people performing an activity',
      ],
      bestForExamples: [
        'what do people who process payroll earn',
        'hourly pay for patient intake work',
        'pay for contract negotiation by geography',
        'how does pay for payroll processing vary across the companies that hire for it',
      ],
      notFor: [
        'Side-by-side pay of two named activities',
        'Pay for an activity at one company — activity pages have no Company filter, so a named company cannot be applied here',
        'Advertised pay on open postings (Activity Demand)',
        'Hiring-market pressure — wage pressure lives on Activity Market Conditions',
      ],
    },
  },
  {
    optionId: '__std__:activity:market-conditions',
    revelioPageIds: ['activity-market-conditions'],
    entity: 'activity',
    pageName: 'Activity Market Conditions',
    requiresResolvedEntity: true,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'How tight the labor market is for an activity — market tightness and demand/supply ratio',
        'The same tightness metrics for up to 6 activities side by side',
        'Wage pressure and time to fill for an activity',
        'Which companies are competing for that work via postings',
      ],
      bestForExamples: [
        'how hard is it to hire people who process payroll',
        'how tight is the market for incident response work',
        'compare hiring difficulty for reviewing code and negotiating contracts',
      ],
      notFor: [
        'Comparing more than 6 activities at once (the primary filter caps at 6)',
        'Hiring difficulty for an activity at one company — activity pages have no Company filter, so a named company cannot be applied here',
        'Posting volume or posting trends (use Activity Demand); individual open postings (use Postings Discovery)',
        'Pay levels — wage pressure here is hiring-market pressure, not pay (use Activity Compensation)',
      ],
    },
  },
  // ---------- APP (standalone) ----------
  {
    optionId: '__std__:app:talent-discovery',
    revelioPageIds: ['talent-discovery', 'talent-discovery-default'],
    entity: 'app',
    pageName: 'Individual Discovery',
    requiresResolvedEntity: false,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Find and filter individual people/profiles',
        'People matching company + role + geography criteria',
        'People matching school attendance, graduation, or alumni criteria',
      ],
      bestForExamples: [
        'find data scientists at Google in London',
        'people who left Microsoft for startups',
        'show me senior engineers in the Bay Area',
        'show headcount for people who attended Northwestern University',
      ],
      notFor: [
        'Aggregate workforce metrics for a company/industry/role/geography/skill/activity (use those pages); a count of profiles matching education history remains an Individual Discovery result set',
        'Ranking companies (use Company Discovery)',
      ],
    },
  },
  {
    optionId: '__std__:app:posting-discovery',
    revelioPageIds: ['posting-discovery', 'posting-discovery-default'],
    entity: 'app',
    pageName: 'Postings Discovery',
    requiresResolvedEntity: false,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Find and filter individual job postings across companies, industries, roles, geographies, skills and activities',
        'Postings matching salary, seniority, title, keyword, or posting-date criteria',
      ],
      bestForExamples: [
        'browse senior engineer postings at Google in London',
        'show me job postings for data scientists in Austin',
        'browse the open roles at Google',
        'postings at Google mentioning Kubernetes',
      ],
      notFor: [
        'Posting volume, demand trends, or metrics for one company/industry/role/geography/skill/activity (use that entity’s Demand page)',
        'Searching for individual people (use Individual Discovery)',
        'Actual hires or headcount (postings ≠ hires)',
        'Applying a remote or on-site constraint for the user — the page has a Remote Type filter, but the assistant cannot set it yet, so the user picks it on the page',
      ],
    },
  },
  {
    optionId: DATA_DICTIONARY_CATALOG_OPTION_ID,
    revelioPageIds: ['data-dictionary'],
    entity: 'app',
    pageName: 'Data Dictionary',
    requiresResolvedEntity: false,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Definitions and formulas for the METRICS and datasets the dashboard reports',
        'How a metric is calculated, and the data methodology behind it — sampling weights, company mapping, reporting lags, how the job and skill taxonomies are derived',
      ],
      bestForExamples: [
        'how is attrition calculated',
        'what does prestige mean',
        'definition of headcount',
        'how are sampling weights applied',
      ],
      notFor: [
        'Any request for actual data or metric values',
        'Browsing the taxonomy’s CONTENTS — which roles, skills or activities exist and how they map to each other (use the Role Overview). This page explains how the taxonomies are BUILT; it does not list them',
      ],
    },
  },
  // ---------- APPLICATIONS (enrichment) ----------
  // These two are the only catalog pages that answer nothing about the
  // workforce. They take a file the USER uploads and hand it back enriched, so
  // every workforce question — however phrased — belongs to some other page.
  // Their `notFor` lists carry that boundary, and the pair's boundary with each
  // other, since the two differ only in what they ingest.
  {
    optionId: '__std__:app:data-enrichment',
    revelioPageIds: ['data-enrichment'],
    entity: 'app',
    pageName: 'Data Enrichment',
    // App-level page that ships under the Deliverables parent.
    navigatePathOverride: '/deliverables/data-enrichment',
    // Nothing to resolve: the page is scoped to the user's own upload, not to a
    // company/industry/geography/role, and holds no filters to land.
    requiresResolvedEntity: false,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Enrich a file the USER uploads — a CSV or Excel list of people (first name, last name, title; company and location columns where present) that Revelio appends columns to',
        'Pick what gets appended: mapped job-taxonomy roles (k10 through k17000, with taxonomy id and match confidence), seniority, standardised location (region/country/state/city/metro area), base-salary, total-compensation and contractor pay-rate benchmarks, and company mapping (RCID, ultimate parent, company LinkedIn)',
        'Submit the mapped file as an enrichment job — the enriched dataset arrives by emailed download link',
      ],
      bestForExamples: [
        'enrich my employee list with roles and seniority',
        'map the job titles in my CSV to Revelio’s role taxonomy',
        'add salary benchmarks to a spreadsheet of people I have',
        'get RCIDs for a list of company names I have',
        'upload a file to enrich',
        'open data enrichment',
      ],
      notFor: [
        'Any question about a real workforce, company, role, or market. This page reads ONLY the rows the user uploads and returns nothing about anyone else — "how many people work at Google", "who is hiring", "what do engineers earn in New York" are analysis asks and belong to the entity pages, Discovery, or a chart, never here',
        'A single résumé or CV — one person, as a PDF (use Resume Enrichment). This page takes a CSV/Excel list of many people',
        'Producing a dataset FROM Revelio’s data by filtering it. This page only adds columns to rows the user already has; extraction is the Data Builder, which the assistant cannot open — say so rather than sending the user here',
        'Checking on, retrieving, or reading back a submitted enrichment — the result is emailed, and neither the page nor the assistant can show it',
        'Charts, metrics, or any analysis of the enriched file once it comes back',
      ],
      selectionCues: [
        'Only when the ask is about the user’s OWN file or list — "my CSV", "our employee roster", "this spreadsheet of titles". If nothing in the ask supplies data, this is not the page',
        'Never the page half of a partial-fit offer or a report ride-along: it answers no analytical ask, so pairing it with a custom dashboard or a report is always wrong',
      ],
    },
  },
  {
    optionId: '__std__:app:resume-enrichment',
    revelioPageIds: ['resume-enrichment'],
    entity: 'app',
    pageName: 'Resume Enrichment',
    // Same reasoning as Data Enrichment: the subject is the uploaded document.
    requiresResolvedEntity: false,
    enabled: true,
    capabilityModel: {
      outcomes: [
        'Parse a résumé the USER uploads (a .pdf) into structured fields — contact details, employment history, education, skills, projects, certifications',
        'Show Revelio-enriched values beside that résumé’s own text: mapped role and job family, mapped skills and activities, seniority, estimated salary range, years of experience, prestige, standardised location, flight risk, remote suitability',
        'Keep the résumés this user has already enriched in one list, to reopen or download as PDF or CSV',
      ],
      bestForExamples: [
        'enrich this resume',
        'parse a CV and map it to Revelio roles and skills',
        'upload a resume to get seniority and a salary estimate',
        'where are the resumes I uploaded',
        'open resume enrichment',
      ],
      notFor: [
        'Any question about a real workforce, company, role, or market — this page reads ONLY the file the user uploads and knows nothing about any measured population (use the entity pages, Discovery, or a chart)',
        'A list of many people in a spreadsheet, CSV or Excel (use Data Enrichment). This page takes one résumé PDF at a time',
        'Finding or screening candidates in Revelio’s profile data — this page never searches, it only reads a document the user already has (use Individual Discovery)',
        'Reading, summarising, or answering questions about the CONTENTS of an uploaded résumé in chat — the assistant cannot see the file; it can only open the page',
        'What an enriched field such as flight risk or prestige MEANS (use the Data Dictionary)',
      ],
      selectionCues: [
        'Only when the ask is about a résumé/CV document the user has — "this resume", "a candidate’s CV", "the resumes I uploaded". A question about people in general is never this page',
        'Never the page half of a partial-fit offer or a report ride-along: it answers no analytical ask, so pairing it with a custom dashboard or a report is always wrong',
      ],
    },
  },
] as const satisfies readonly GlobalAssistantPageCatalogEntry[];
